import { Link, useLocation } from "react-router-dom";
import { Bed, Bath, Users, MapPin, Moon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { scrollToBooking } from "@/lib/scroll-to-booking";
import { prefetchProperty } from "@/lib/prefetch-property";
import { withServiceFee } from "@/lib/pricing";
import FavoriteButton from "@/components/FavoriteButton";
import StarRating from "@/components/reviews/StarRating";
import { usePropertyRatings } from "@/hooks/usePropertyRatings";
import { SignedImage } from "@/components/SignedImage";


interface PropertyCardProps {
  property: {
    id: string;
    title: string;
    slug: string;
    image: string;
    price_per_night: number;
    price_per_night_low?: number | null;
    cleaning_fee?: number | null;
    bedrooms: number;
    beds: number;
    bathrooms: number;
    sleeps: number;
    amenities: string[];
    description: string;
    tagline: string;
    unit_number?: string | null;
    street_address?: string | null;
    internal_listing_name?: string | null;
    min_nights?: number | null;
  };
  /** Query string (e.g. "check_in=...&check_out=...&guests=2") carried over from the search bar. */
  searchQuery?: string;
}

const PropertyCard = ({ property, searchQuery }: PropertyCardProps) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const qs = searchQuery ? `?${searchQuery}` : "";
  const detailPath = `/property/${property.slug}${qs}`;
  const { data: ratings } = usePropertyRatings();
  const rating = ratings?.[property.id];


  const handleBookNow = (e: React.MouseEvent) => {
    if (location.pathname === `/property/${property.slug}`) {
      e.preventDefault();
      scrollToBooking();
    }
  };

  // Warm the detail-page query on hover/focus so navigation feels instant.
  const warm = () => prefetchProperty(queryClient, property.slug);

  return (
    <div
      className="group bg-card rounded-lg overflow-hidden shadow-card ring-1 ring-border/40 hover:shadow-elevated hover:ring-border/60 transition-all duration-300 hover:-translate-y-1"
      onMouseEnter={warm}
      onFocus={warm}
    >
      <div className="relative h-64 overflow-hidden">
        <Link
          to={detailPath}
          className="block h-full w-full"
          aria-label={`View details for ${property.title}`}
        >
          <SignedImage
            src={property.image}
            alt={property.title}
            className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.06]"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300" />
        </Link>
        <FavoriteButton propertyId={property.id} title={property.title} className="z-10" />
      </div>
      <div className="p-6">
        <p className="text-xs font-body uppercase tracking-widest text-accent font-medium mb-1">
          {property.tagline}
        </p>
        <h3 className="font-display text-xl font-semibold text-foreground mb-1">
          {property.title}
        </h3>
        {rating && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <StarRating value={rating.average} size={14} />
            <span className="font-body text-sm font-semibold text-foreground">
              {rating.average.toFixed(1)}
            </span>
            <span className="font-body text-xs text-muted-foreground">
              ({rating.count} {rating.count === 1 ? "review" : "reviews"})
            </span>
          </div>
        )}
        {property.price_per_night ? (() => {
          // Show the raw low-season baseline (or fall back to standard rate).
          // Cleaning and service fees are surfaced on the booking widget.
          const high = Number(property.price_per_night);
          const low = Number(property.price_per_night_low ?? 0);
          const baseline = low > 0 ? Math.min(high, low) : high;
          return (
            <p className="font-body text-sm text-muted-foreground mb-2">
              <span className="text-xs">From </span>
              <span className="font-display text-lg font-semibold text-foreground">
                ${Math.round(baseline).toLocaleString()}
              </span>{" "}
              <span className="text-xs">/ night</span>
            </p>
          );
        })() : null}
        {(property.street_address || property.unit_number) && (
          <p className="text-sm font-body text-muted-foreground mb-3 flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {property.street_address}
            {property.street_address && property.unit_number && (
              <span className="text-muted-foreground">|</span>
            )}
            {property.unit_number && (
              <span>Unit {property.unit_number}</span>
            )}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-muted-foreground text-sm font-body mb-4">
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <Bed className="h-4 w-4 shrink-0" /> {property.bedrooms} BR
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <Bed className="h-4 w-4 shrink-0" /> {property.beds} Beds
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <Bath className="h-4 w-4 shrink-0" /> {property.bathrooms} BA
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <Users className="h-4 w-4 shrink-0" /> Sleeps {property.sleeps}
          </span>
          {property.min_nights ? (
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <Moon className="h-4 w-4 shrink-0" /> {property.min_nights} night{property.min_nights !== 1 ? "s" : ""} min
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          {property.amenities.slice(0, 4).map((amenity) => (
            <span
              key={amenity}
              className="text-xs font-body bg-muted text-muted-foreground px-2.5 py-1 rounded-full"
            >
              {amenity}
            </span>
          ))}
          {property.amenities.length > 4 && (
            <span className="text-xs font-body text-accent font-medium px-2.5 py-1">
              +{property.amenities.length - 4} more
            </span>
          )}
        </div>
        <div className="flex gap-3">
          <Link to={detailPath} className="flex-1">
            <Button variant="secondary" className="w-full font-body rounded-full bg-muted text-foreground hover:bg-muted/80">
              View Details
            </Button>
          </Link>
          <Link to={`${detailPath}#booking`} onClick={handleBookNow} className="flex-1">
            <Button className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-body rounded-full">
              Book Now
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;
